import * as React from 'react';
import Form, { useForm, useWatch } from 'antd/lib/form/Form';
import Modal from 'antd/lib/modal';
import FormItem from 'antd/lib/form/FormItem';
import Select from 'antd/lib/select';
import Text from 'antd/lib/typography/Text';
import { antGrey } from '@/components/styles/globalStyles';
import {
    ASCCustomProductPageState,
    AppStoreReviewSubmissionItemType,
    AppleAppStoreData,
    CustomProductPage,
    InAppEvent,
    InAppEventState,
} from '@/types';
import { reviewItemNameForType } from '@/helpers/displayHelpers';
import { idForSubmissionItem, renderReviewsSubmissionItemCard } from '@/components/StepContentSubmission/helpers';
import { PlusOutlined } from '@ant-design/icons';
import { Nullable, StepRunComponentProps } from '@/typescriptHelpers';

const SUBMISSION_ITEM_CATEGORIES = [
    AppStoreReviewSubmissionItemType.CustomProductPage,
    AppStoreReviewSubmissionItemType.AppEvent,
    // AppStoreReviewSubmissionItemType.AppStoreVersionExperiment, // This is apparently not supported along with an app version
];

const getSubmissionItemsForCategory = (
    category: AppStoreReviewSubmissionItemType,
    appStoreData: AppleAppStoreData,
): (CustomProductPage | InAppEvent)[] | null => {
    if (!appStoreData) {
        return null;
    }

    const currentlyIncludedReviewSubmissionItemId =
        appStoreData?.relevantReviewSubmission?.reviewSubmissionItems?.map((item) => item.itemId) ?? [];

    switch (category) {
        case AppStoreReviewSubmissionItemType.CustomProductPage:
            return appStoreData.customProductPages.filter(
                (item) => !currentlyIncludedReviewSubmissionItemId.includes(item.id),
            );
        case AppStoreReviewSubmissionItemType.AppEvent:
            return appStoreData.inAppEvents.filter(
                (item) => !currentlyIncludedReviewSubmissionItemId.includes(item.id),
            );
        default:
            return null;
    }
};

const nameForSubmissionItem = (
    item: CustomProductPage | InAppEvent,
    category: AppStoreReviewSubmissionItemType,
): string => {
    switch (category) {
        case AppStoreReviewSubmissionItemType.CustomProductPage:
            return (item as CustomProductPage).name;
        case AppStoreReviewSubmissionItemType.AppEvent:
            return (item as InAppEvent).referenceName;
        default:
            return '';
    }
};

export interface AddReviewSubmissionItemsModalProps {
    isOpen: boolean;
    onClose: () => void;
    displayableVersion: string;
    appStoreData: AppleAppStoreData;
    onAddItem: (itemId: string, category: AppStoreReviewSubmissionItemType) => void;
    isLoading: boolean;
    alreadyIncludedItemIdsMap: Record<string, boolean>;
    stepRunComponentProps: Nullable<StepRunComponentProps>;
}

export const AddReviewSubmissionItemsModal: React.FC<AddReviewSubmissionItemsModalProps> = ({
    isOpen,
    onClose,
    displayableVersion,
    appStoreData,
    onAddItem,
    isLoading,
    alreadyIncludedItemIdsMap,
    stepRunComponentProps,
}) => {
    const [form] = useForm();

    const itemId = useWatch('itemId', form);

    return (
        <Modal
            title={`Add an item to ${displayableVersion} submission`}
            onOk={() => {
                if (itemId) {
                    form.submit();
                }
            }}
            okText={`Add item to ${displayableVersion} submission`}
            okButtonProps={{
                icon: <PlusOutlined />,
                disabled: !itemId,
                loading: isLoading,
            }}
            onCancel={onClose}
            open={isOpen}
            destroyOnClose
        >
            <div
                style={{
                    display: 'flex',
                    flexDirection: 'column',
                    width: '100%',
                    gap: 16,
                }}
            >
                <Text
                    style={{
                        fontSize: 14,
                        color: antGrey.grey8,
                    }}
                >
                    Select an existing item to add to your {displayableVersion} submission package. On submitting for
                    review, all items will be included together.
                </Text>
                <Form
                    form={form}
                    preserve={false}
                    layout="vertical"
                    onFinish={(values) => {
                        const { itemId, category } = values;
                        onAddItem(itemId, category);
                    }}
                >
                    <FormItem label="Choose a category of submission item:" name="category">
                        <Select
                            placeholder="Select a category"
                            onChange={() => {
                                form.setFieldsValue({ itemId: undefined });
                            }}
                        >
                            {SUBMISSION_ITEM_CATEGORIES.map((category) => {
                                const itemsCount = getSubmissionItemsForCategory(category, appStoreData)?.length ?? 0;

                                return (
                                    <Select.Option key={category} value={category} disabled={itemsCount === 0}>
                                        {reviewItemNameForType(category)} ({itemsCount}{' '}
                                        {itemsCount === 1 ? 'item' : 'items'})
                                    </Select.Option>
                                );
                            })}
                        </Select>
                    </FormItem>

                    <FormItem shouldUpdate>
                        {(form) => {
                            const category = form.getFieldValue('category');

                            const submissionItems = getSubmissionItemsForCategory(category, appStoreData);

                            if (!submissionItems) {
                                return null;
                            }

                            return (
                                <FormItem label={`Choose a ${reviewItemNameForType(category)}:`} name="itemId">
                                    <Select>
                                        {submissionItems.map((item) => {
                                            const id = idForSubmissionItem(item, category);
                                            if (!id) {
                                                return null;
                                            }

                                            let isInUnselectableState = true;

                                            switch (category) {
                                                case AppStoreReviewSubmissionItemType.CustomProductPage: {
                                                    const customProductPage = item as CustomProductPage;
                                                    const state = customProductPage.versions?.[0]?.state;
                                                    if (
                                                        state === ASCCustomProductPageState.ReadyForReview ||
                                                        state === ASCCustomProductPageState.PrepareForSubmission
                                                    ) {
                                                        isInUnselectableState = false;
                                                    }
                                                    break;
                                                }
                                                case AppStoreReviewSubmissionItemType.AppEvent: {
                                                    const inAppEvent = item as InAppEvent;
                                                    if (
                                                        inAppEvent.eventState === InAppEventState.ReadyForReview ||
                                                        inAppEvent.eventState === InAppEventState.Draft
                                                    ) {
                                                        isInUnselectableState = false;
                                                    }
                                                    break;
                                                }
                                            }

                                            if (alreadyIncludedItemIdsMap[id]) {
                                                isInUnselectableState = true;
                                            }

                                            return (
                                                <Select.Option key={id} value={id} disabled={isInUnselectableState}>
                                                    {nameForSubmissionItem(item, category)}
                                                </Select.Option>
                                            );
                                        })}
                                    </Select>
                                </FormItem>
                            );
                        }}
                    </FormItem>
                    <FormItem shouldUpdate>
                        {(form) => {
                            const itemId = form.getFieldValue('itemId');
                            const category = form.getFieldValue('category');

                            if (!itemId || !category) {
                                return null;
                            }

                            const submissionItems = getSubmissionItemsForCategory(category, appStoreData);

                            if (!submissionItems) {
                                return null;
                            }

                            const item = submissionItems.find((item) => idForSubmissionItem(item, category) === itemId);

                            if (!item) {
                                return null;
                            }

                            return renderReviewsSubmissionItemCard({
                                item,
                                type: category,
                                reviewSubmissionItemId: '',
                                shouldHideASCLink: true,
                                stepRunComponentProps,
                            });
                        }}
                    </FormItem>
                </Form>
            </div>
        </Modal>
    );
};
